import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
    TextField,
    InputAdornment,
    IconButton,
    Tooltip,
    Chip,
    Alert,
    CircularProgress,
    Skeleton,
    Drawer,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    Snackbar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import InfoIcon from "@mui/icons-material/Info";
import ArticleIcon from "@mui/icons-material/Article";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { getScannedFiles, getIngestionProgressLog, deleteSharePointFile } from "../../services/apiService";
import FileStatusChip from "./shared/FileStatusChip";
import { formatDate, formatSize } from "./shared/formatters";
import SummaryDialog from "../SummaryDialog";
import FileProcessingProgress from "../FilesList/FileProcessingProgress";

const POPPINS = { fontFamily: "'Poppins', sans-serif" };
const ROWS_PER_PAGE_OPTIONS = [10, 20, 50];
const HEADER_COLOR = "#1B2559";
const PRIMARY_COLOR = "#1976d2";
const TOTAL_PIPELINE_STEPS = 26;          // known pipeline step count
const PROCESSING_TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2 h

/** Returns true when a started/in-progress file has been running too long */
const hasProcessingTimedOut = (startTime) => {
    if (!startTime) return false;
    try {
        return Date.now() - new Date(startTime).getTime() > PROCESSING_TIMEOUT_MS;
    } catch {
        return false;
    }
};

/** Derive 0-100 completion percentage from ingestion_progress.sequence_number */
const getProgressPercent = (file) =>
    Math.min(Math.round(((file.sequence_number ?? 0) / TOTAL_PIPELINE_STEPS) * 100), 100);

const TableSkeleton = ({ rows = 8, cols = 7 }) => (
    <>
        {Array.from({ length: rows }).map((_, i) => (
            <TableRow key={i}>
                {Array.from({ length: cols }).map((__, j) => (
                    <TableCell key={j}>
                        <Skeleton variant="text" animation="wave" />
                    </TableCell>
                ))}
            </TableRow>
        ))}
    </>
);

TableSkeleton.propTypes = { rows: PropTypes.number, cols: PropTypes.number };

/**
 * SharePointHub — full-page table of all scanned SharePoint files for the
 * current user.  Supports search, pagination, and row-level actions
 * (view versions, find similar).
 *
 * @param {{ onViewVersions: (file) => void, onFindSimilar: (file) => void }} props
 */
const SharePointHub = ({ onViewVersions, onFindSimilar }) => {
    const [files, setFiles] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    // Search (client-side filter applied on top of the loaded page)
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    // Summary dialog
    const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
    const [selectedSummaryFile, setSelectedSummaryFile] = useState(null);

    const handleGenerateSummary = (file) => {
        setSelectedSummaryFile(file.job_run_id);
        setSummaryDialogOpen(true);
    };

    const handleCloseSummaryDialog = () => {
        setSummaryDialogOpen(false);
        setSelectedSummaryFile(null);
    };

    // Processing log drawer (left-side collapsible)
    const [logDrawerOpen, setLogDrawerOpen] = useState(false);
    const [logFile, setLogFile] = useState(null);          // { name, run_id }
    const [logStatus, setLogStatus] = useState(null);      // raw pipeline array
    const [logLoading, setLogLoading] = useState(false);

    const handleOpenLog = async (file) => {
        const fileName = file.name;
        const runId = file.run_id || file.job_run_id || null;
        setLogFile({ name: fileName, run_id: runId });
        setLogStatus(null);
        setLogLoading(true);
        setLogDrawerOpen(true);
        try {
            const res = await getIngestionProgressLog(fileName, runId);
            if (res?.data && Array.isArray(res.data)) {
                setLogStatus(res.data);
            } else if (res?.status) {
                setLogStatus(res.status);
            } else {
                setLogStatus([]);
            }
        } catch {
            setLogStatus([]);
        } finally {
            setLogLoading(false);
        }
    };

    const handleCloseLog = () => {
        setLogDrawerOpen(false);
        setLogFile(null);
        setLogStatus(null);
    };

    // Delete file
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [fileToDelete, setFileToDelete] = useState(null); // file object
    const [deleting, setDeleting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const handleDeleteClick = (file) => {
        setFileToDelete(file);
        setDeleteConfirmOpen(true);
    };

    const handleCancelDelete = () => {
        setDeleteConfirmOpen(false);
        setFileToDelete(null);
    };

    const handleConfirmDelete = async () => {
        const file = fileToDelete;
        setDeleteConfirmOpen(false);
        setFileToDelete(null);
        setDeleting(true);
        try {
            const response = await deleteSharePointFile(file.item_id);
            if (response && !response.error) {
                setSnackbar({ open: true, message: `"${file.name}" deleted successfully. Please wait for Table view to refresh.`, severity: "success" });
                fetchFiles(true);
            } else {
                throw new Error(response?.error || response?.message || "Delete failed");
            }
        } catch (err) {
            setSnackbar({ open: true, message: `Failed to delete: ${err.message}`, severity: "error" });
        } finally {
            setDeleting(false);
        }
    };

    // Debounce search input 400 ms
    useEffect(() => {
        const id = setTimeout(() => setDebouncedSearch(search), 400);
        return () => clearTimeout(id);
    }, [search]);

    const fetchFiles = useCallback(
        async (silent = false) => {
            if (!silent) setLoading(true);
            else setRefreshing(true);
            setError(null);
            try {
                const res = await getScannedFiles({
                    limit: rowsPerPage,
                    offset: page * rowsPerPage,
                });
                if (res?.error) {
                    setError(res.message || "Failed to load files.");
                    return;
                }
                const rows = res?.data ?? res ?? [];
                setFiles(Array.isArray(rows) ? rows : []);
                setTotal(res?.total ?? rows.length);
            } catch (err) {
                setError("An unexpected error occurred.");
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [page, rowsPerPage]
    );

    useEffect(() => {
        fetchFiles();
    }, [fetchFiles]);

    const handleChangePage = (_, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const handleSearchClear = () => {
        setSearch("");
        setPage(0);
    };

    // Filter loaded data client-side — no extra API call on search
    const filteredFiles = debouncedSearch
        ? files.filter((f) =>
            (f.name || "").toLowerCase().includes(debouncedSearch.toLowerCase())
        )
        : files;

    const resolveStatus = (file) => {
        if (file.soft_delete) return "deleted";
        const s = (file.status || "").toLowerCase();
        if (s === "completed") return "completed";
        if (s.includes("in_progress") || s.includes("started")) {
            return hasProcessingTimedOut(file.start_time) ? "failed" : "in_progress";
        }
        if (s.includes("fail") || s.includes("error")) return "failed";
        return "not_processed";
    };

    // Auto-refresh every 30 s while any file is still being processed
    useEffect(() => {
        const hasInProgress = files.some((f) => resolveStatus(f) === "in_progress");
        if (!hasInProgress) return;
        const id = setInterval(() => fetchFiles(true), 30_000);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files, fetchFiles]);

    return (
        <Box sx={{ p: 2 }}>
            {/* Toolbar */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                    gap: 2,
                    flexWrap: "wrap",
                }}
            >
                <Typography
                    variant="h6"
                    sx={{ ...POPPINS, fontWeight: 700, color: HEADER_COLOR }}
                >

                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextField
                        size="small"
                        placeholder="Search by File Name…"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(0);
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{ color: "#9e9e9e" }} />
                                </InputAdornment>
                            ),
                            endAdornment: search ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={handleSearchClear}>
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                            sx: { ...POPPINS, fontSize: "0.85rem" },
                        }}
                        sx={{ width: 240 }}
                    />

                    <Tooltip title="Refresh">
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => fetchFiles(true)}
                                disabled={refreshing}
                                sx={{ color: PRIMARY_COLOR }}
                            >
                                {refreshing ? (
                                    <CircularProgress size={18} color="inherit" />
                                ) : (
                                    <RefreshIcon />
                                )}
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            {/* Error */}
            {error && (
                <Alert severity="error" sx={{ mb: 2, ...POPPINS }}>
                    {error}
                </Alert>
            )}

            {/* Table */}
            <Paper elevation={1} sx={{ borderRadius: 2, overflow: "hidden" }}>
                <TableContainer>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                {["File Name", "Size", "Version", "Modified", "Status", "Message", "Actions"].map(
                                    (h) => (
                                        <TableCell
                                            key={h}
                                            sx={{
                                                ...POPPINS,
                                                fontWeight: 700,
                                                fontSize: "0.8rem",
                                                color: HEADER_COLOR,
                                                backgroundColor: "#f5f7fb",
                                            }}
                                        >
                                            {h}
                                        </TableCell>
                                    )
                                )}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading || deleting ? (
                                <TableSkeleton rows={rowsPerPage} cols={7} />
                            ) : filteredFiles.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={POPPINS}
                                        >
                                            {debouncedSearch
                                                ? `No files matching "${debouncedSearch}"`
                                                : "No scanned files found. Upload files to SharePoint to get started."}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredFiles.map((file) => {
                                    const status = resolveStatus(file);
                                    return (
                                        <TableRow
                                            key={file.item_id}
                                            hover
                                            sx={{
                                                opacity: status === "deleted" ? 0.55 : 1,
                                                "&:last-child td": { border: 0 },
                                            }}
                                        >
                                            {/* Name */}
                                            <TableCell sx={{ maxWidth: 300 }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <InsertDriveFileOutlinedIcon
                                                        sx={{ color: PRIMARY_COLOR, fontSize: 18, flexShrink: 0 }}
                                                    />
                                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                                        <Tooltip title={file.name}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    ...POPPINS,
                                                                    fontWeight: 500,
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                    maxWidth: 470,
                                                                }}
                                                            >
                                                                {file.name || "—"}
                                                            </Typography>
                                                        </Tooltip>
                                                        {status === "in_progress" && (
                                                            <Box sx={{ mt: 0.5 }}>
                                                                <LinearProgress
                                                                    variant="determinate"
                                                                    value={getProgressPercent(file)}
                                                                    sx={{ height: 3, borderRadius: 2, mb: 0.25 }}
                                                                />
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{ ...POPPINS, color: "text.secondary", fontSize: "0.68rem" }}
                                                                >
                                                                    Step {file.sequence_number ?? 0} of {TOTAL_PIPELINE_STEPS} ({getProgressPercent(file)}%)
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                    {/* {file.web_url && (
                                                        <Tooltip title="Open in SharePoint">
                                                            <IconButton
                                                                size="small"
                                                                component="a"
                                                                href={file.web_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                sx={{ p: 0.25, color: "#9e9e9e", flexShrink: 0 }}
                                                            >
                                                                <OpenInNewIcon sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )} */}
                                                </Box>
                                            </TableCell>

                                            {/* Size */}
                                            <TableCell>
                                                <Typography variant="body2" sx={{ ...POPPINS, color: "#555" }}>
                                                    {formatSize(file.size_bytes)}
                                                </Typography>
                                            </TableCell>

                                            {/* Version */}
                                            <TableCell>
                                                <Chip
                                                    label={`v${file.version_number ?? 1}`}
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => !refreshing && !deleting && onViewVersions && onViewVersions(file)}
                                                    disabled={refreshing || deleting}
                                                    sx={{ ...POPPINS, fontSize: "0.7rem", color: PRIMARY_COLOR, borderColor: PRIMARY_COLOR }}
                                                />
                                            </TableCell>

                                            {/* Modified */}
                                            <TableCell>
                                                <Typography variant="body2" sx={{ ...POPPINS, color: "#555", whiteSpace: "nowrap" }}>
                                                    {formatDate(file.modified_at || file.scanned_at)}
                                                </Typography>
                                            </TableCell>

                                            {/* Status */}
                                            <TableCell>
                                                <FileStatusChip status={status} />
                                            </TableCell>

                                            {/* Message — only shown when pipeline did not complete all steps */}
                                            <TableCell sx={{ maxWidth: 260 }}>
                                                {file.message && file.sequence_number !== 25 ? (
                                                    <Tooltip title={file.message}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                ...POPPINS,
                                                                fontSize: "0.78rem",
                                                                color: "#555",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap",
                                                                maxWidth: 240,
                                                            }}
                                                        >
                                                            {file.message}
                                                        </Typography>
                                                    </Tooltip>
                                                ) : (
                                                    <Typography variant="body2" sx={{ ...POPPINS, color: "#bbb" }}>—</Typography>
                                                )}
                                            </TableCell>

                                            {/* Actions */}
                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    {/* <Tooltip title="Version History">
                            <IconButton
                              size="small"
                              onClick={() => onViewVersions && onViewVersions(file)}
                              sx={{ color: PRIMARY_COLOR }}
                            >
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip> */}
                                                    {/* {status === "completed" && (
                            <Tooltip title="Find Similar Files">
                              <IconButton
                                size="small"
                                onClick={() => onFindSimilar && onFindSimilar(file)}
                                sx={{ color: "#7b61ff" }}
                              >
                                <AutoAwesomeIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )} */}
                                                    {status === "completed" && (
                                                        <Tooltip title="View Summary">
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    disabled={refreshing || deleting}
                                                                    onClick={() => handleGenerateSummary(file)}
                                                                    sx={{ color: PRIMARY_COLOR }}
                                                                >
                                                                    <InfoIcon fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                    {status !== "not_processed" && status !== "deleted" && (
                                                        <Tooltip title="Processing Log">
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    disabled={refreshing || deleting}
                                                                    onClick={() => handleOpenLog(file)}
                                                                    sx={{
                                                                        color:
                                                                            logFile?.name === file.name && logDrawerOpen
                                                                                ? "#e65100"
                                                                                : "#78909c",
                                                                    }}
                                                                >
                                                                    <ArticleIcon fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                    {status !== "deleted" && (
                                                        <Tooltip title={status === "in_progress" ? "Cannot delete while processing" : "Delete File"}>
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    disabled={deleting || refreshing || status === "in_progress"}
                                                                    onClick={() => handleDeleteClick(file)}
                                                                >
                                                                    <DeleteOutlineIcon fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: "1px solid #e0e0e0" }}>
                    <Typography
                        variant="caption"
                        sx={{
                            ...POPPINS,
                            pl: 2,
                            color: "#f57c00",
                            fontStyle: "italic",
                            flexShrink: 0,
                        }}
                    >
                        ⏱ Currently platform supports PPF & PPT documents.
                    </Typography>
                    <TablePagination
                        component="div"
                        count={debouncedSearch ? filteredFiles.length : total}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                        sx={{ ...POPPINS, fontSize: "0.8rem" }}
                    />
                </Box>
            </Paper>

            <SummaryDialog
                open={summaryDialogOpen}
                onClose={handleCloseSummaryDialog}
                selectedFile={selectedSummaryFile}
            />

            {/* Delete confirmation dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={handleCancelDelete}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        ...POPPINS,
                        fontWeight: 600,
                        color: HEADER_COLOR,
                        borderBottom: "1px solid #E2E8F0",
                        py: 2,
                        px: 3,
                    }}
                >
                    Confirm Delete
                </DialogTitle>
                <DialogContent sx={{ pt: 2, pb: 1, px: 3 }}>
                    <Typography sx={{ ...POPPINS, fontSize: "0.95rem", mt: 1 }}>
                        Are you sure you want to delete{" "}
                        <strong>{fileToDelete?.name}</strong>? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, px: 3, py: 2 }}>
                    <Button
                        onClick={handleCancelDelete}
                        sx={{ textTransform: "none", ...POPPINS }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                        disabled={deleting}
                        sx={{ textTransform: "none", ...POPPINS }}
                    >
                        Delete
                    </Button>
                </Box>
            </Dialog>

            {/* Feedback snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                message={snackbar.message}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                ContentProps={{
                    sx: {
                        ...POPPINS,
                        backgroundColor: snackbar.severity === "error" ? "#d32f2f" : "#2e7d32",
                    },
                }}
            />

            {/* Processing log — right collapsible Drawer */}
            <Drawer
                anchor="right"
                open={logDrawerOpen}
                onClose={handleCloseLog}
                PaperProps={{ sx: { width: { xs: "100%", sm: 420 }, p: 0 } }}
            >
                {/* Drawer header */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2,
                        py: 1.5,
                        borderBottom: "1px solid #e0e0e0",
                        flexShrink: 0,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                        <ArticleIcon sx={{ color: "#e65100", fontSize: 20, flexShrink: 0 }} />
                        <Typography
                            variant="subtitle2"
                            sx={{
                                ...POPPINS,
                                fontWeight: 700,
                                color: HEADER_COLOR,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        // title={logFile?.name}
                        >
                            {/* {logFile?.name ?? "Processing Log"} */}
                            Processing Log
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={handleCloseLog}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                {/* Loading bar */}
                {logLoading && (
                    <LinearProgress
                        sx={{ height: 3, flexShrink: 0 }}
                    />
                )}

                {/* Body */}
                <Box sx={{ flex: 1, overflowY: "auto", p: 1.5 }}>
                    {!logLoading && logStatus !== null && (
                        <FileProcessingProgress
                            fileName={logFile?.name ?? ""}
                            pipelineStatus={logStatus}
                        />
                    )}
                    {!logLoading && logStatus === null && (
                        <Typography variant="body2" color="text.secondary" sx={{ ...POPPINS, p: 1 }}>
                            No log data available.
                        </Typography>
                    )}
                </Box>
            </Drawer>
        </Box>
    );
};

SharePointHub.propTypes = {
    onViewVersions: PropTypes.func,
    onFindSimilar: PropTypes.func,
};

export default SharePointHub;
